import React, { useCallback, useState } from "react";
import { StandardModal, StandardModalBody } from "@design-system/Modal/Modal";
import { GUSTO_PREVIEW_URL } from "@constants";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CreateGustoConnection } from "@components/gusto/CreateGustoConnection";
import { useCreateGustoAccountMutation } from "@api/gusto.api";
import { useAllowFeature } from "@hooks/useAllowFeature";
import {
    FLAT_RATE_PLANS,
    flatRatePlanNames,
    PREMIUM_FEATURES,
} from "@common/flatRateBilling";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { usePlanManagement } from "@components/billing/PlanManagement/PlanManagementContext";
import styles from "./GustoConnectionModal.module.scss";

interface Props {
    show: boolean;
    onHide?: () => void;
}

export const GustoConnectionModal: React.FC<Props> = ({ show, onHide }) => {
    const [creatingAccount, setCreatingAccount] = useState<boolean>(false);

    // Here we need to check if the workspace has access to GUSTO feature on the plan.
    const { hasAccess } = useAllowFeature(PREMIUM_FEATURES.GUSTO);
    const { upgrade } = usePlanManagement();

    const createGustoAccountMutation = useCreateGustoAccountMutation();
    const handleCreateGustoAccount = useCallback(async () => {
        setCreatingAccount(true);

        await createGustoAccountMutation
            .mutateAsync()
            .then((accountClaimUrl) => {
                if (accountClaimUrl) {
                    window.open(
                        accountClaimUrl,
                        "_blank",
                        "noopener noreferrer",
                    );
                }
            })
            .finally(() => {
                setCreatingAccount(false);
            });
    }, [setCreatingAccount, createGustoAccountMutation]);

    const handleUpgrade = useCallback(() => {
        upgrade(FLAT_RATE_PLANS.BASIC, false);
    }, [upgrade]);

    return (
        <StandardModal show={show} onHide={onHide}>
            <StandardModalBody>
                <h4 className={styles.headline}>
                    {hasAccess ? (
                        <>Connect your Gusto account</>
                    ) : (
                        <>
                            Gusto is available on{" "}
                            <span className="text-blue-600">Basic</span>
                        </>
                    )}
                </h4>
                <p className="my-4">
                    Automate payroll, taxes, and contractor payment
                    reconciliation in Kick. No payroll? Create a new account
                    through Kick and get 3 months of Gusto free.
                </p>
                <div className={styles.preview}>
                    <img
                        className="w-100"
                        src={GUSTO_PREVIEW_URL}
                        alt="Gusto feature preview"
                    />
                </div>
                <HStack justify="end" gap={12} className="mt-4">
                    <ButtonWithLoader
                        variant="primary"
                        size="xl"
                        onClick={handleCreateGustoAccount}
                        loading={creatingAccount}
                    >
                        Create account
                    </ButtonWithLoader>
                    {hasAccess ? (
                        <CreateGustoConnection
                            btnVariant="primary"
                            btnSize="xl"
                        >
                            Connect account
                        </CreateGustoConnection>
                    ) : (
                        <Button
                            variant="tertiary"
                            size="xl"
                            onClick={handleUpgrade}
                        >
                            Upgrade to{" "}
                            {flatRatePlanNames[FLAT_RATE_PLANS.BASIC]}
                        </Button>
                    )}
                </HStack>
            </StandardModalBody>
        </StandardModal>
    );
};
